import { randomizeBodyPoseXZYaw, setBodyPose, restoreBaselineSnapshot } from "../sceneOps";
import type { BodyUUID } from "../../types/scene";
import type { BTLeaf } from "../types";
import type { LeafContext, TickResult } from "./context";

export function tickResetToBaseline(
  ctx: LeafContext,
  _node: Extract<BTLeaf, { kind: "resetToBaseline" }>
): TickResult {
  void _node;
  if (!ctx.doc.baselineSnapshot) {
    ctx.setFailureReason("Reset baseline failed: no baseline snapshot has been captured.");
    console.warn("[behavior] resetToBaseline: no baseline captured");
    return "failure";
  }
  const ok = restoreBaselineSnapshot(ctx.doc.baselineSnapshot);
  if (!ok) {
    ctx.setFailureReason("Reset baseline failed: could not restore snapshot.");
    return "failure";
  }
  return "success";
}

export function tickRandomizeObjectPose(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "randomizeObjectPose" }>
): TickResult {
  if (!node.bodyUuid) {
    ctx.setFailureReason("Randomize object failed: no target body selected.");
    return "failure";
  }
  const ok = randomizeBodyPoseXZYaw({
    bodyUuid: node.bodyUuid as BodyUUID,
    radiusXY: node.radiusXY,
    yawRangeRad: (node.yawRangeDeg * Math.PI) / 180,
  });
  if (!ok) {
    ctx.setFailureReason("Randomize object failed: selected target body is missing.");
    return "failure";
  }
  return "success";
}

export function tickSetObjectPose(
  ctx: LeafContext,
  node: Extract<BTLeaf, { kind: "setObjectPose" }>
): TickResult {
  if (!node.bodyUuid) {
    ctx.setFailureReason("Set object pose failed: no target body selected.");
    return "failure";
  }
  const ok = setBodyPose({
    bodyUuid: node.bodyUuid as BodyUUID,
    position: [node.x, node.y, node.z],
    orientation: [node.qw, node.qx, node.qy, node.qz],
  });
  if (!ok) {
    ctx.setFailureReason("Set object pose failed: target body is missing.");
    return "failure";
  }
  return "success";
}

export function tickWait(ctx: LeafContext, node: Extract<BTLeaf, { kind: "wait" }>): TickResult {
  const lm = ctx.getLeafMem(node.id);
  const now = performance.now();
  const sec = Math.max(0, Number(node.seconds) || 0);
  if (lm.waitUntilMs == null) {
    lm.waitUntilMs = now + sec * 1000;
  }
  if (now >= (lm.waitUntilMs ?? 0)) {
    ctx.clearLeafMem(node.id);
    return "success";
  }
  ctx.setActiveNode(node.id);
  return "running";
}
